/**
 * Measmerize Add To Cart integration.
 * - Populates measmerize sizes
 * - Exposes operations.addToCart
 * - Dispatches cart:update for theme cart components
 */
(function () {
  /**
   * @param {unknown} value
   * @returns {string}
   */
  function normalizeSize(value) {
    return String(value ?? '')
      .trim()
      .toLowerCase();
  }

  /**
   * @param {unknown} a
   * @param {unknown} b
   * @returns {boolean}
   */
  function sizeMatches(a, b) {
    const na = normalizeSize(a);
    const nb = normalizeSize(b);
    if (na === nb) return true;
    const fa = parseFloat(na.replace(',', '.'));
    const fb = parseFloat(nb.replace(',', '.'));
    return !Number.isNaN(fa) && !Number.isNaN(fb) && fa === fb;
  }

  /**
   * @typedef {{ value: string, isAvailable: boolean, variantId: number }} SizeRow
   * @typedef {{ id: number, title?: string, option1?: string, option2?: string, option3?: string, available: boolean }} VariantRow
   * @typedef {{ productId: number, productCode: string | null, cartAddUrl: string, sizes: SizeRow[], variants: VariantRow[] }} MeasmerizeProductConfig
   */

  /**
   * @param {MeasmerizeProductConfig} config
   * @param {string} productSize
   * @returns {SizeRow | null}
   */
  function findVariantForSize(config, productSize) {
    const sizes = config.sizes || [];
    for (let i = 0; i < sizes.length; i++) {
      const row = sizes[i];
      if (row && sizeMatches(row.value, productSize)) return row;
    }

    const variants = config.variants || [];
    for (let i = 0; i < variants.length; i++) {
      const row = variants[i];
      if (!row) continue;
      if (
        sizeMatches(row.title, productSize) ||
        sizeMatches(row.option1, productSize) ||
        sizeMatches(row.option2, productSize) ||
        sizeMatches(row.option3, productSize)
      ) {
        return {
          value: String(row.title || row.option1 || ''),
          isAvailable: Boolean(row.available),
          variantId: row.id,
        };
      }
    }

    return null;
  }

  /**
   * @returns {string[]}
   */
  function getCartSectionIds() {
    /** @type {string[]} */
    const ids = [];
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        ids.push(el.dataset.sectionId);
      }
    });
    return ids;
  }

  /**
   * @param {number} variantId
   * @param {number} quantity
   * @param {string} cartAddUrl
   * @returns {Promise<Record<string, unknown> & { status?: number, message?: string, sections?: Record<string, string>, item_count?: number, items?: Array<{ quantity?: number }> }>}
   */
  async function addVariantToCart(variantId, quantity, cartAddUrl) {
    const rawPath = String(cartAddUrl || '/cart/add.js').trim();
    const path = rawPath.startsWith('http')
      ? rawPath
      : rawPath.startsWith('/')
      ? rawPath
      : `/${rawPath}`;
    const sectionIds = getCartSectionIds();

    const body = {
      items: [{ id: variantId, quantity }],
      sections: sectionIds.length ? sectionIds.join(',') : undefined,
      sections_url: window.location.pathname,
    };

    const response = await fetch(path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    });

    const data = /** @type {Awaited<ReturnType<typeof addVariantToCart>>} */ (await response.json());
    if (!response.ok || data.status) {
      throw new Error(data.message || 'Cart add failed');
    }

    return data;
  }

  /**
   * @param {Record<string, unknown> & { sections?: Record<string, string>, item_count?: number, items?: Array<{ quantity?: number }> }} response
   * @param {string} productId
   */
  function dispatchCartUpdate(response, productId) {
    const items = Array.isArray(response.items) ? response.items : [];
    const itemCount =
      typeof response.item_count === 'number'
        ? response.item_count
        : items.reduce((total, item) => total + (Number(item?.quantity) || 0), 0);

    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: response,
          sourceId: 'measmerize-add-to-cart',
          data: {
            source: 'measmerize',
            itemCount,
            productId,
            sections: response.sections,
          },
        },
      })
    );
  }

  /**
   * @returns {MeasmerizeProductConfig | null}
   */
  function readConfig() {
    const el = document.getElementById('measmerize-product-data');
    if (!el || !el.textContent) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      console.error('[Measmerize] Invalid product config', error);
      return null;
    }
  }

  /**
   * @param {MeasmerizeProductConfig} config
   */
  function installMeasmerizeOperations(config) {
    const win = /** @type {any} */ (window);
    win.measmerize = win.measmerize || {};
    win.measmerize.sizes = win.measmerize.sizes || {};
    win.measmerize.operations = win.measmerize.operations || {};

    win.measmerize.sizes.default = (config.sizes || []).map((size) => ({
      value: String(size.value),
      isAvailable: Boolean(size.isAvailable),
    }));

    win.measmerize.operations.addToCart = async (
      /** @type {string} */ productCode,
      /** @type {string} */ productSize,
      /** @type {number} */ quantity = 1
    ) => {
      const expectedCode = config.productCode ? String(config.productCode) : '';
      if (expectedCode && productCode != null && String(productCode) !== expectedCode) {
        return false;
      }

      const matched = findVariantForSize(config, String(productSize));
      if (!matched || !matched.variantId || !matched.isAvailable) {
        return false;
      }

      try {
        const qty = Math.max(1, parseInt(String(quantity), 10) || 1);
        const response = await addVariantToCart(matched.variantId, qty, config.cartAddUrl);
        dispatchCartUpdate(response, String(config.productId));
        return true;
      } catch (error) {
        console.error('[Measmerize] addToCart failed', error);
        return false;
      }
    };
  }

  /**
   * @param {MeasmerizeProductConfig} config
   */
  function init(config) {
    const scriptEl = document.getElementById('measmerize-script');
    if (!scriptEl) return;

    const handleReady = () => {
      installMeasmerizeOperations(config);
    };

    scriptEl.addEventListener('measmerize.widget.ready', handleReady, { once: true });
    document.addEventListener('measmerize.widget.ready', handleReady, { once: true });
    window.addEventListener('measmerize.widget.ready', handleReady, { once: true });

    // Fallback for race conditions where ready is emitted before listeners.
    installMeasmerizeOperations(config);
  }

  function boot() {
    const config = readConfig();
    if (!config) return;
    init(config);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();